import { useState, useEffect, useRef } from 'react'
import { Flex } from 'rebass/styled-components'
import styled, { keyframes } from 'styled-components';
import { toast } from 'react-toastify';
import ReactTooltip from 'react-tooltip';
import { Menu as MenuInner, MenuItem, MenuButton, FocusableItem } from '@szhsin/react-menu';
import {
  menuSelector,
  menuItemSelector,
  menuDividerSelector
} from "@szhsin/react-menu/style-utils";
import '@szhsin/react-menu/dist/core.css';
import { useMutation, useQuery } from '@apollo/client';
import { IGetHelpscoutUsersOutput, IIGetHelpscoutUsersInput, IThreadOutput } from '../../../graphql/types/queries/conversation';
import { GET_CONVERSATIONS, GET_CONVERSATION_BY_ID, GET_HELPSCOUT_USERS } from '../../../graphql/queries';
import { IUser } from '../../../graphql/types/queries/user';
import { InputNoForm } from '../../../components/TextInput';
import { CHANGE_CONVERSATION_STATUS, REASSIGN_CONVERSATION } from '../../../graphql/mutations';
import { IProviderChangeStatusConversationInput, IProviderChangeStatusConversationOutput, IProviderReassignConversationInput, IProviderReassignConversationOutput } from '../../../graphql/types/mutations/conversation';

const menuShow = keyframes`
  from {
    opacity: 0;
  }
`;
const menuHide = keyframes`
  to {
    opacity: 0;
  }
`;

export const Menu = styled(MenuInner)`
  ${menuSelector.name} {
    font-size: 0.925rem;
    user-select: none;
    box-shadow: 1px 1px 20px 1px rgba(0, 0, 0, 0.1);
    border-radius: 6px;
    padding: 6px;
    min-width: 10rem;
  }

  ${menuSelector.stateOpening} {
    animation: ${menuShow} 0.15s ease-out;
  }

  // NOTE: animation-fill-mode: forwards is required to
  // prevent flickering with React 18 createRoot()
  ${menuSelector.stateClosing} {
    animation: ${menuHide} 0.2s ease-out forwards;
  }

  ${menuItemSelector.name} {
    border-radius: 6px;
    padding: 0.375rem 0.625rem;
  }

  ${menuItemSelector.hover} {
    color: #fff;
    background-color: #59a2ff;
  }

  ${menuDividerSelector.name} {
    margin: 0.5rem 0.625rem;
  }

  ${menuItemSelector.submenu} {
    position: relative;
    &::after {
      position: absolute;
      width: 7px;
      right: 0.625rem;
    }
  }
`;

interface IConversationToolbarProps {
  onReply: () => void
  isReplying: boolean
  onNote: () => void
  isNoting: boolean
  session: IUser
  conversation: IThreadOutput
  refetchConversation: () => void
}

const Button = styled(MenuButton) <{ background?: string }>`
  width: 52px;
  height: 42px;
  position: relative;
  justify-content: center;
  align-items: center;
  border: 0;
  background: ${props => props.background ? props.background : 'white'};
  display: flex;
  cursor: pointer;
  transition:all 0.3s ease;
  &.disabled {
    cursor: not-allowed;
    background: transparent;
  }
  &.isToggled {
  background-color: #e5e9ec;
  }
  &:hover{
  background-color: #e5e9ec;
  }
`

export const ToolbarMenuButton = Button

export const ConversationToolbar: React.FC<IConversationToolbarProps> = ({ onNote, isNoting, onReply, isReplying, session, conversation, refetchConversation }) => {
  const { data, loading, error } = useQuery<IGetHelpscoutUsersOutput, IIGetHelpscoutUsersInput>(GET_HELPSCOUT_USERS)
  const [reassignConversation, { data: ReassignConversationData, loading: loadingReassignConversation, error: errorReassignConversation }] = useMutation<IProviderReassignConversationOutput, IProviderReassignConversationInput>(REASSIGN_CONVERSATION, {
    refetchQueries: [{
      query: GET_CONVERSATIONS, variables: {
        status: 'closed'
      }
    }, {
      query: GET_CONVERSATIONS, variables: {
        status: 'open'
      }
    }, {
      query: GET_CONVERSATION_BY_ID,
      variables: {
        conversationId: conversation.id.toString()
      }
    }],
    onError: (e) => {
      if (e && toastIdChangeAssignedTo && toastIdChangeAssignedTo.current) {
        toast.update(toastIdChangeAssignedTo.current, {
          render: 'Failed to reassign the conversation',
          type: toast.TYPE.ERROR,
          isLoading: false,
          autoClose: 5000,
          closeButton: null // The closeButton defined on ToastContainer will be used
        });
      }
    }
  })
  const [changeStatusConversation, { data: ChangeStatusConversationData, loading: loadingChangeStatusConversation, error: errorChangeStatusConversation }] = useMutation<IProviderChangeStatusConversationOutput, IProviderChangeStatusConversationInput>(CHANGE_CONVERSATION_STATUS, {
    refetchQueries: [{
      query: GET_CONVERSATIONS, variables: {
        status: 'closed'
      }
    }, {
      query: GET_CONVERSATIONS, variables: {
        status: 'open'
      }
    }, {
      query: GET_CONVERSATION_BY_ID,
      variables: {
        conversationId: conversation.id.toString()
      }
    }],
    onError: (e) => {
      if (e && toastIdChangeStatus && toastIdChangeStatus.current) {
        toast.update(toastIdChangeStatus.current, {
          render: 'Failed to change the conversation status',
          type: toast.TYPE.ERROR,
          isLoading: false,
          autoClose: 5000,
          closeButton: null // The closeButton defined on ToastContainer will be used
        });
      }
    }
  })

  const [filter, setFilter] = useState<string>('');
  const [users, setUsers] = useState<Array<{
    label: string,
    value: number,
  }> | null>(null);
  const toastIdChangeStatus = useRef<any>(null);
  const toastIdChangeAssignedTo = useRef<any>(null);

  useEffect(() => {
    if (!!loading || !data?.getHelpscoutUsers) return

    const u = [{
      label: 'Anyone',
      value: -1,
    }]

    u.push(...data?.getHelpscoutUsers.filter(x => x.type === 'user').sort((a, b) => {
      if (a.id === session.helpscoutId) return -1
      return 1
    }).map(x => {
      return {
        label: x.id === session.helpscoutId ? 'Me' : `${x.firstName} ${x.lastName}`,
        value: x.id,
      }
    }))
    setUsers(u)
  }, [data, loading])
  return (
    <Flex style={{ borderBottom: '1px solid #e5e9ec', width: '100%', paddingLeft: '30px' }}>
      <Button data-tip='Reply' data-for='tooltipConversationToolbar' disabled={isNoting} style={{ opacity: isNoting ? 0.3 : 1 }} onClick={onReply} className={isReplying ? 'isToggle' : isNoting ? 'disabled' : ''}>
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M5 9.5H15C15.162 9.5 19 9.5 19 13.5C19 18 15.298 18 15 18H7" stroke="#556575" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M8.5 13L5 9.5L8.5 6" stroke="#556575" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </Button>
      <Button data-for='tooltipConversationToolbar2' data-tip='Note' style={{ opacity: isReplying ? 0.3 : 1 }} onClick={onNote} className={isNoting ? 'isToggle' : isReplying ? 'disabled' : ''}>
        <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18.7993 12L19.9993 13.2L7.86602 25.3334H6.66602V24.1334L18.7993 12ZM23.5994 4.00002C23.3327 4.00002 22.9327 4.13335 22.666 4.40002L20.266 6.80002L25.1993 11.8667L27.5994 9.33335C28.1327 8.80002 28.1327 8.00002 27.5994 7.46669L24.5327 4.40002C24.266 4.13335 23.866 4.00002 23.5994 4.00002ZM18.7993 8.26669L3.99935 22.9334V28H9.06602L23.7327 13.2L18.7993 8.26669V8.26669ZM9.33268 2.66669V6.66669H13.3327V9.33335H9.33268V13.3334H6.66602V9.33335H2.66602V6.66669H6.66602V2.66669H9.33268Z" fill="#556575" />
        </svg>
      </Button>
      <Menu
        onMenuChange={e => e.open && setFilter('')}
        transition
        menuButton={
          <Button disabled={isReplying || isNoting || !!loadingReassignConversation} data-for='tooltipConversationToolbar3' data-tip='Assign' style={{ opacity: isReplying || isNoting ? 0.3 : 1 }} className={isReplying || isNoting ? 'disabled' : ''}>
            <Flex style={{ marginLeft: '-6px' }}>
              <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M26.8282 23.8625C26.2387 22.4662 25.3832 21.1978 24.3095 20.1281C23.2389 19.0553 21.9708 18.2 20.5751 17.6094C20.5626 17.6031 20.5501 17.6 20.5376 17.5938C22.4845 16.1875 23.7501 13.8969 23.7501 11.3125C23.7501 7.03125 20.2813 3.5625 16.0001 3.5625C11.7188 3.5625 8.25008 7.03125 8.25008 11.3125C8.25008 13.8969 9.5157 16.1875 11.4626 17.5969C11.4501 17.6031 11.4376 17.6062 11.4251 17.6125C10.0251 18.2031 8.76883 19.05 7.6907 20.1313C6.61792 21.2018 5.76259 22.4699 5.17195 23.8656C4.59171 25.232 4.27877 26.697 4.25008 28.1812C4.24924 28.2146 4.25509 28.2478 4.26728 28.2788C4.27947 28.3099 4.29776 28.3382 4.32105 28.3621C4.34435 28.386 4.3722 28.4049 4.40294 28.4179C4.43369 28.4308 4.46672 28.4375 4.50008 28.4375H6.37508C6.51258 28.4375 6.62195 28.3281 6.62508 28.1938C6.68758 25.7813 7.65633 23.5219 9.36883 21.8094C11.1407 20.0375 13.4938 19.0625 16.0001 19.0625C18.5063 19.0625 20.8595 20.0375 22.6313 21.8094C24.3438 23.5219 25.3126 25.7813 25.3751 28.1938C25.3782 28.3313 25.4876 28.4375 25.6251 28.4375H27.5001C27.5334 28.4375 27.5665 28.4308 27.5972 28.4179C27.628 28.4049 27.6558 28.386 27.6791 28.3621C27.7024 28.3382 27.7207 28.3099 27.7329 28.2788C27.7451 28.2478 27.7509 28.2146 27.7501 28.1812C27.7188 26.6875 27.4095 25.2344 26.8282 23.8625V23.8625ZM16.0001 16.6875C14.5657 16.6875 13.2157 16.1281 12.2001 15.1125C11.1845 14.0969 10.6251 12.7469 10.6251 11.3125C10.6251 9.87812 11.1845 8.52812 12.2001 7.5125C13.2157 6.49687 14.5657 5.9375 16.0001 5.9375C17.4345 5.9375 18.7845 6.49687 19.8001 7.5125C20.8157 8.52812 21.3751 9.87812 21.3751 11.3125C21.3751 12.7469 20.8157 14.0969 19.8001 15.1125C18.7845 16.1281 17.4345 16.6875 16.0001 16.6875Z" fill="#556575" />
              </svg>
              <svg width="16" height="16" style={{ position: 'absolute', left: 30, top: 14 }} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21.724 12.3907L16 18.1147L10.276 12.3907L8.39062 14.276L16 21.8854L23.6093 14.276L21.724 12.3907Z" fill="#556575" />
              </svg>
            </Flex>
          </Button>
        }>
        {!users ? (<MenuItem>Loading</MenuItem>) : (
          <>
            <FocusableItem>
              {({ ref }) => (
                <InputNoForm style={{ borderRadius: 0, height: '28px', fontSize: '12px' }} ref={ref} type="text" placeholder="Type to filter"
                  value={filter} onChange={(e: any) => setFilter(e.target.value)} />
              )}
            </FocusableItem>
            {users.filter(x => x.label.toLowerCase().indexOf(filter.toLowerCase()) > -1).map(x => {
              return <MenuItem key={x.value}
                onClick={async () => {
                  if (toastIdChangeAssignedTo && toastIdChangeAssignedTo.current) {
                    toast.dismiss(toastIdChangeAssignedTo.current)
                  }
                  toastIdChangeAssignedTo.current = toast(`Reassigning to ${x.label}`, { autoClose: false, isLoading: true, closeButton: false });
                  await reassignConversation({
                    variables: {
                      conversationId: conversation.id,
                      assignedTo: x.value
                    }
                  })
                  toast.update(toastIdChangeAssignedTo.current, {
                    render: 'Conversation reassigned',
                    type: toast.TYPE.SUCCESS,
                    isLoading: false,
                    autoClose: 5000,
                    closeButton: null // The closeButton defined on ToastContainer will be used
                  });
                }}>{x.label}</MenuItem>
            })}
          </>
        )}
      </Menu>
      <Menu transition menuButton={
        <Button disabled={isReplying || isNoting || !!loadingChangeStatusConversation} data-for='tooltipConversationToolbar4' data-tip='Status' style={{ opacity: isReplying || isNoting ? 0.3 : 1 }} className={isReplying || isNoting ? 'disabled' : ''}>
          <Flex style={{ marginLeft: '-6px' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5.5 20.5C5.23478 20.5 4.98043 20.3947 4.79289 20.2071C4.60536 20.0196 4.5 19.7652 4.5 19.5V4.50002C4.5 4.23481 4.60536 3.98045 4.79289 3.79292C4.98043 3.60538 5.23478 3.50002 5.5 3.50002H11.88C12.0664 3.49873 12.2494 3.54954 12.4084 3.64672C12.5674 3.7439 12.6961 3.88359 12.78 4.05002L13.5 5.50002H18.5C18.7652 5.50002 19.0196 5.60538 19.2071 5.79292C19.3946 5.98045 19.5 6.23481 19.5 6.50002V14.5C19.5 14.7652 19.3946 15.0196 19.2071 15.2071C19.0196 15.3947 18.7652 15.5 18.5 15.5H13.11C12.9253 15.4995 12.7444 15.4478 12.5873 15.3507C12.4302 15.2536 12.3031 15.1149 12.22 14.95L11.5 13.5H6.5V19.5C6.5 19.7652 6.39464 20.0196 6.20711 20.2071C6.01957 20.3947 5.76522 20.5 5.5 20.5V20.5ZM6.5 5.50002V11.5H12.5L13.5 13.5H17.5V7.50002H12.5L11.5 5.50002H6.5Z" fill="#556575" />
            </svg>

            <svg width="16" height="16" style={{ position: 'absolute', left: 30, top: 14 }} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21.724 12.3907L16 18.1147L10.276 12.3907L8.39062 14.276L16 21.8854L23.6093 14.276L21.724 12.3907Z" fill="#556575" />
            </svg>
          </Flex>
        </Button>
      }>
        <MenuItem onClick={async () => {
          if (toastIdChangeStatus && toastIdChangeStatus.current) {
            toast.dismiss(toastIdChangeStatus.current)
          }
          toastIdChangeStatus.current = toast(`Changing conversation status to OPEN`, { autoClose: false, isLoading: true, closeButton: false });
          await changeStatusConversation({
            variables: {
              conversationId: conversation.id,
              status: 'open',
              userId: session.helpscoutId
            }
          })
          toast.update(toastIdChangeStatus.current, {
            render: 'Conversation status is now OPEN',
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            autoClose: 5000,
            closeButton: null // The closeButton defined on ToastContainer will be used
          });
          // refetchConversation()
        }}>Opened</MenuItem>
        <MenuItem onClick={async () => {
          if (toastIdChangeStatus && toastIdChangeStatus.current) {
            toast.dismiss(toastIdChangeStatus.current)
          }
          toastIdChangeStatus.current = toast(`Changing conversation status to PENDING`, { autoClose: false, isLoading: true, closeButton: false });
          await changeStatusConversation({
            variables: {
              conversationId: conversation.id,
              status: 'pending',
              userId: session.helpscoutId
            }
          })
          toast.update(toastIdChangeStatus.current, {
            render: 'Conversation status is now PENDING',
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            autoClose: 5000,
            closeButton: null // The closeButton defined on ToastContainer will be used
          });
        }}>Pending</MenuItem>
        <MenuItem onClick={async () => {
          if (toastIdChangeStatus && toastIdChangeStatus.current) {
            toast.dismiss(toastIdChangeStatus.current)
          }
          toastIdChangeStatus.current = toast(`Changing conversation status to CLOSED`, { autoClose: false, isLoading: true, closeButton: false });
          await changeStatusConversation({
            variables: {
              conversationId: conversation.id,
              status: 'closed',
              userId: session.helpscoutId
            }
          })
          toast.update(toastIdChangeStatus.current, {
            render: 'Conversation status is now CLOSED',
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            autoClose: 5000,
            closeButton: null // The closeButton defined on ToastContainer will be used
          });
        }}>Closed</MenuItem>
      </Menu>
      <ReactTooltip id='tooltipConversationToolbar' place="bottom" type="dark" effect="solid" />
      <ReactTooltip id='tooltipConversationToolbar2' place="bottom" type="dark" effect="solid" />
      <ReactTooltip id='tooltipConversationToolbar3' place="bottom" type="dark" effect="solid" />
      <ReactTooltip id='tooltipConversationToolbar4' place="bottom" type="dark" effect="solid" />
      {/* <Modal
        isOpen={!!loadingChangeStatusConversation || !!loadingReassignConversation}>
        <Flex flexDirection={'column'}

          style={{
            margin: '0 auto',
            alignSelf: 'center',
            borderRadius: '20px',
            position: 'relative',
            backgroundColor: 'white',
            padding: '24px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            // overflow: 'hidden'
          }}>
          <PleaseWait text=' '>

          </PleaseWait>
        </Flex>
      </Modal> */}
    </Flex>);
}